import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  Building2,
  Check,
  Compass,
  Cpu,
  Crosshair,
  Globe2,
  Landmark,
  Layers3,
  MapPinned,
  Mail,
  MessageSquare,
  Orbit,
  Rocket,
  Sparkles,
  Star,
  Store,
  Ticket,
  Users2,
  UserRound,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { PROJECT_CREATOR } from '../constants/contacts';
import { EXPERIENCE_IMAGE_MAP } from '../data/experienceImageMap';
import { getExperiences } from '../services/legacyApiService';
import { resolveExperienceCoverCandidates } from '../utils/imageUtils';

const EDU_EMAIL = 'info@thethinkingclouds.com';

const buildMailtoLink = (subject: string, body: string) =>
  `mailto:${EDU_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const capabilityGroups = [
  {
    title: 'Crea esperienze AI-native',
    description: 'Da contenuti statici a percorsi narrativi, geolocalizzati e conversazionali in pochi step.',
  },
  {
    title: 'Configura guide virtuali',
    description: 'Ogni luogo diventa una guida intelligente con tono, stile narrativo e conoscenza dedicata.',
  },
  {
    title: 'Pubblica multi-canale',
    description: 'Distribuisci su web e mobile, valorizzando immagini, audio, testi e interazioni contestuali.',
  },
  {
    title: 'Misura e ottimizza',
    description: 'Analytics, governance e gestione operativa per scalare progetti culturali B2B e B2B2C.',
  },
];

const audiences = [
  {
    icon: UserRound,
    title: 'Creator',
    description: 'Trasforma storie, format e contenuti in esperienze digitali pubblicabili.',
    value: 'Dalla tua idea al percorso live, con strumenti visuali e AI.',
  },
  {
    icon: Landmark,
    title: 'Musei',
    description: 'Rendi collezioni e sale esperienze interattive accessibili e multilingua.',
    value: 'Dalla scheda opera alla guida virtuale in conversazione.',
  },
  {
    icon: Building2,
    title: 'Territori e organizzazioni',
    description: 'Coordina eventi, itinerari, punti geolocalizzati e storytelling locale.',
    value: 'Una piattaforma viva per comuni, enti culturali e reti turistiche.',
  },
];

const targetSegments = [
  { title: 'Per musei', text: 'Digitalizza collezioni e sale in percorsi guidati con AI, accessibilità e narrazione multilingua.' },
  { title: 'Per territori e comuni', text: 'Trasforma mappe e luoghi in atlanti narrativi geolocalizzati con tappe, eventi e contenuti live.' },
  { title: 'Per creator', text: 'Passa dall’idea alla pubblicazione con workflow visuale, card modulari e guida virtuale pronta.' },
  { title: 'Per organizzazioni ed enti culturali', text: 'Governa progetti complessi, team e distribuzione con una console unica orientata al risultato.' },
  { title: 'Per eventi e tour operator', text: 'Costruisci esperienze pre/durante/post evento con percorsi dinamici e interazioni contestuali.' },
  { title: 'Per formazione e progetti educativi', text: 'Rendi i contenuti didattici esperienze vive, dialogiche e memorabili per studenti e visitatori.' },
];

const heroMarkers = [
  { top: '18%', left: '18%' },
  { top: '26%', left: '72%' },
  { top: '60%', left: '28%' },
  { top: '68%', left: '80%' },
];

const aiPipeline = ['Contenuto', 'Esperienza', 'Guida virtuale', 'Conversazione'];
const gloociStoryMoments = [
  {
    title: 'Un luogo non e solo una posizione',
    text: 'Con Glooci ogni luogo diventa un punto narrativo: tappe, voci, media e dialoghi contestuali.',
  },
  {
    title: 'Un contenuto non resta statico',
    text: 'Testi, immagini, audio e curiosita si trasformano in percorsi interattivi guidati da AI.',
  },
  {
    title: 'Una visita diventa esperienza',
    text: 'Utenti, turisti e community esplorano, ascoltano, chiedono e scoprono in modo naturale.',
  },
];

type ShowcaseExperience = {
  id: number;
  name: string;
  city?: string;
  image: string;
};

function buildMappedFallbackShowcase(): ShowcaseExperience[] {
  const entries = Object.entries(EXPERIENCE_IMAGE_MAP).slice(0, 8);
  return entries
    .map(([id, filename], index) => ({
      id: Number(id),
      name: `Esperienza ${index + 1}`,
      city: 'Territorio culturale',
      image: resolveExperienceCoverCandidates({ id: Number(id), image: filename })[0] || '',
    }))
    .filter((item) => Boolean(item.image));
}

const APP_VISUAL_FALLBACKS: ShowcaseExperience[] = [
  { id: 90001, name: 'Percorsi e mappe', city: 'Glooci App', image: '/a.png' },
  { id: 90002, name: 'Guide virtuali', city: 'Glooci App', image: '/b.png' },
  { id: 90003, name: 'Story card', city: 'Glooci App', image: '/c.png' },
  { id: 90004, name: 'Esperienze live', city: 'Glooci App', image: '/d.png' },
];

const Landing = () => {
  const [experienceShowcase, setExperienceShowcase] = useState<ShowcaseExperience[]>([]);

  const eduMailto = useMemo(() => {
    return buildMailtoLink(
      'Richiesta piano EDU Glooci Platform',
      `Ciao,

vorrei ricevere informazioni sul piano EDU di Glooci Platform per scuole o universita.

Nome ente:
Referente:
Numero utenti previsti:
Esigenze principali:

Grazie.`,
    );
  }, []);

  const pricingMailto = useMemo(() => {
    return buildMailtoLink(
      'Richiesta pricing Glooci Platform',
      `Ciao,

vorrei ricevere informazioni su pricing e attivazione della Glooci Platform.

Nome:
Organizzazione:
Tipo di progetto:
Numero esperienze previste:

Grazie.`,
    );
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadShowcase = async () => {
      try {
        const rows = await getExperiences();
        const pickedFromApi = rows
          .map((exp) => ({
            id: Number(exp.id || 0),
            name: exp.name || 'Esperienza Glooci',
            city: exp.city || undefined,
            image: resolveExperienceCoverCandidates(exp)[0] || '',
          }))
          .filter((item) => item.id > 0 && item.image)
          .slice(0, 6);
        const pickedFromMap = buildMappedFallbackShowcase().slice(0, 6);
        const merged = [...pickedFromApi, ...pickedFromMap]
          .filter((item, index, arr) => arr.findIndex((entry) => entry.image === item.image) === index)
          .slice(0, 6);
        const finalItems = merged.length > 0 ? merged : APP_VISUAL_FALLBACKS;
        if (mounted) setExperienceShowcase(finalItems);
      } catch {
        const pickedFromMap = buildMappedFallbackShowcase().slice(0, 6);
        if (mounted) setExperienceShowcase(pickedFromMap.length > 0 ? pickedFromMap : APP_VISUAL_FALLBACKS);
      }
    };
    void loadShowcase();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    document.title = 'Glooci | Piattaforma AI per esperienze digitali';
    const ensureMeta = (selector: string, attribute: 'name' | 'property', key: string, content: string) => {
      let node = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!node) {
        node = document.createElement('meta');
        node.setAttribute(attribute, key);
        document.head.appendChild(node);
      }
      node.setAttribute('content', content);
    };
    ensureMeta(
      'meta[name="description"]',
      'name',
      'description',
      'Glooci e la piattaforma per creare esperienze digitali con guide virtuali AI, percorsi geolocalizzati e storytelling interattivo per musei, creator, territori e organizzazioni.',
    );
    ensureMeta('meta[property="og:title"]', 'property', 'og:title', 'Glooci | Esperienze digitali guidate da AI');
    ensureMeta(
      'meta[property="og:description"]',
      'property',
      'og:description',
      'Trasforma contenuti in percorsi, guide virtuali e conversazioni intelligenti. Piattaforma B2B/B2B2C per musei, territori, creator ed eventi.',
    );
    ensureMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    ensureMeta('meta[property="og:image"]', 'property', 'og:image', '/glooci_logo.png');
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[linear-gradient(180deg,#f4f8ff_0%,#ffffff_34%,#f8fbff_100%)] text-slate-900">
      <header className="sticky top-0 z-40 border-b border-white/70 bg-white/65 backdrop-blur-xl">
        <div className="container-max flex items-center justify-between gap-4 px-6 py-3 md:px-8">
          <Link to="/landing" className="inline-flex items-center gap-3">
            <div className="rounded-2xl border border-white/70 bg-white/85 px-3 py-2 shadow-[0_10px_35px_rgba(15,23,42,0.08)]">
              <img src="/glooci_logo.png" alt="Glooci" className="h-8 w-auto" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Glooci Platform</p>
              <p className="text-base font-semibold text-slate-900">Esperienze digitali AI</p>
            </div>
          </Link>
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 lg:flex">
            <a href="#piattaforma" className="transition hover:text-slate-950">Piattaforma</a>
            <a href="#ai-guide" className="transition hover:text-slate-950">AI & Guide</a>
            <a href="#target" className="transition hover:text-slate-950">Per chi</a>
            <a href="#community" className="transition hover:text-slate-950">Community</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="rounded-full border border-slate-300/80 bg-white/80 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-900 hover:text-slate-900">
              Accedi
            </Link>
            <a href={pricingMailto} className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-slate-800">
              Richiedi demo <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </header>

      <section className="relative isolate overflow-hidden border-b border-slate-200/70 bg-[radial-gradient(circle_at_14%_0%,rgba(76,130,255,0.32),transparent_38%),radial-gradient(circle_at_84%_8%,rgba(250,204,21,0.22),transparent_30%),radial-gradient(circle_at_64%_36%,rgba(56,189,248,0.2),transparent_34%),linear-gradient(180deg,#eaf2ff_0%,#ffffff_56%,#f7fbff_100%)]">
        <motion.div
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="pointer-events-none absolute inset-0"
        >
          <div className="absolute left-[10%] top-[15%] h-56 w-56 rounded-full bg-blue-300/30 blur-3xl" />
          <div className="absolute right-[8%] top-[8%] h-48 w-48 rounded-full bg-cyan-300/35 blur-3xl" />
          <div className="absolute bottom-[8%] left-[35%] h-44 w-44 rounded-full bg-amber-200/40 blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06)_1px,transparent_1px),linear-gradient(0deg,rgba(37,99,235,0.06)_1px,transparent_1px)] bg-[size:56px_56px]" />
          <svg className="absolute inset-0 h-full w-full opacity-40" viewBox="0 0 1000 600" preserveAspectRatio="none" aria-hidden>
            <path d="M90,470 C220,390 290,260 430,290 C580,320 650,220 860,250" fill="none" stroke="url(#heroPath)" strokeWidth="2.5" strokeDasharray="10 8" />
            <path d="M130,180 C280,140 400,220 540,190 C660,165 760,110 910,120" fill="none" stroke="url(#heroPath2)" strokeWidth="2" strokeDasharray="7 10" />
            <defs>
              <linearGradient id="heroPath" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#0ea5e9" />
                <stop offset="50%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
              <linearGradient id="heroPath2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#2563eb" />
                <stop offset="100%" stopColor="#06b6d4" />
              </linearGradient>
            </defs>
          </svg>
          {heroMarkers.map((marker) => (
            <motion.div
              key={`${marker.top}-${marker.left}`}
              className="absolute"
              style={{ top: marker.top, left: marker.left }}
              animate={{ y: [0, -6, 0], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="relative">
                <div className="h-3 w-3 rounded-full bg-sky-500 shadow-[0_0_0_6px_rgba(14,165,233,0.2)]" />
                <div className="absolute -left-2 -top-2 h-7 w-7 rounded-full border border-sky-400/50" />
              </div>
            </motion.div>
          ))}
        </motion.div>
        <div className="container-max relative px-6 py-10 md:px-8 md:py-14">
          <div className="flex flex-col gap-12">
            <div className="grid items-center gap-10 lg:grid-cols-[1.08fr_0.92fr]">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.6 }}
                className="max-w-3xl"
              >
                <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-indigo-300/60 bg-white/85 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
                  <Sparkles size={16} className="text-amber-600" />
                  Piattaforma AI per esperienze culturali e territoriali
                </div>
                <h1 className="max-w-4xl font-['Merriweather'] text-[44px] leading-[1.02] text-slate-950 md:text-6xl">
                  Glooci e la piattaforma per creare esperienze digitali, guide virtuali e percorsi narrativi con AI.
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 md:text-xl">
                  Non una semplice guida digitale: Glooci trasforma luoghi, contenuti e storie in esperienze
                  interattive geolocalizzate con conversazioni intelligenti su web e app.
                </p>
                <p className="mt-4 text-sm font-semibold text-slate-500">
                  Per musei, creator, territori, comuni, organizzazioni culturali ed eventi.
                </p>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <a
                    href={pricingMailto}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-500 px-6 py-4 text-base font-semibold text-slate-950 shadow-[0_18px_40px_rgba(217,148,9,0.26)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(217,148,9,0.35)] hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    Richiedi demo piattaforma
                    <ArrowRight size={18} />
                  </a>
                  <a
                    href="#target"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white/85 px-6 py-4 text-base font-semibold text-slate-700 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-slate-900 hover:text-slate-950 hover:shadow-md"
                  >
                    Esplora casi d'uso
                    <Compass size={18} />
                  </a>
                </div>

                <div className="mt-8 flex flex-wrap gap-4 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 shadow-sm">
                    <Check size={15} className="text-emerald-600" />
                    AI conversazionale inclusa
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 shadow-sm">
                    <Check size={15} className="text-emerald-600" />
                    Percorsi geolocalizzati
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 shadow-sm">
                    <Check size={15} className="text-emerald-600" />
                    Esperienze per B2B/B2B2C
                  </span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.65, delay: 0.1 }}
                className="relative"
              >
                <div className="absolute -left-12 top-8 hidden h-32 w-32 rounded-full bg-amber-300/45 blur-3xl lg:block" />
                <div className="absolute -right-10 bottom-14 hidden h-36 w-36 rounded-full bg-indigo-300/35 blur-3xl lg:block" />
                <div className="relative overflow-hidden rounded-[34px] border border-white/70 bg-white/82 p-6 shadow-[0_28px_90px_rgba(15,23,42,0.14)] backdrop-blur">
                  <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(56,189,248,0.08),transparent_45%,rgba(99,102,241,0.08))]" />
                  <div className="relative">
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Glooci Experience Engine</p>
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">online</span>
                    </div>

                    <div className="grid gap-4">
                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                          <MapPinned size={13} className="text-sky-600" />
                          Mappa narrativa
                        </div>
                        <div className="relative h-28 overflow-hidden rounded-xl bg-[linear-gradient(135deg,#eef6ff,#f8fbff)]">
                          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 360 120" aria-hidden>
                            <path d="M12,98 C70,80 106,38 166,52 C216,64 244,28 346,24" fill="none" stroke="#38bdf8" strokeWidth="2.5" strokeDasharray="8 7" />
                          </svg>
                          <motion.div animate={{ x: [0, 8, 0], y: [0, -5, 0] }} transition={{ duration: 3.6, repeat: Infinity }} className="absolute left-[22%] top-[54%] h-3 w-3 rounded-full bg-sky-500 shadow-[0_0_0_7px_rgba(14,165,233,0.24)]" />
                          <motion.div animate={{ x: [0, -6, 0], y: [0, 4, 0] }} transition={{ duration: 4, repeat: Infinity }} className="absolute left-[64%] top-[28%] h-3 w-3 rounded-full bg-indigo-500 shadow-[0_0_0_7px_rgba(99,102,241,0.22)]" />
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                          <Bot size={13} className="text-indigo-600" />
                          Guida virtuale AI
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="max-w-[85%] rounded-xl bg-slate-100 px-3 py-2 text-slate-700">Mostrami il percorso per scoprire il centro storico.</div>
                          <div className="ml-auto max-w-[88%] rounded-xl bg-indigo-600 px-3 py-2 text-white">Ti guido tra tappe, storie e contenuti interattivi del territorio.</div>
                        </div>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center text-xs font-semibold text-slate-700">Contenuto</div>
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center text-xs font-semibold text-slate-700">Percorso</div>
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center text-xs font-semibold text-slate-700">Conversazione</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section id="piattaforma" className="container-max px-6 py-20 md:px-8">
        <div className="mb-10 rounded-[30px] border border-slate-200 bg-[linear-gradient(130deg,#ffffff,#f4f8ff)] p-6 shadow-[0_16px_34px_rgba(15,23,42,0.06)] md:p-8">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
            <MapPinned size={13} className="text-sky-600" />
            DNA Glooci
          </div>
          <h2 className="max-w-3xl font-['Merriweather'] text-3xl leading-tight text-slate-950 md:text-4xl">
            Glooci trasforma contenuti, luoghi e racconti in esperienze digitali interattive.
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-600">
            Non una landing SaaS generica: una piattaforma pensata per turismo culturale, musei, territori e creator
            che vogliono costruire esperienze vive, conversazionali e geolocalizzate.
          </p>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            {gloociStoryMoments.map((item, index) => (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35, delay: index * 0.06 }}
                className="rounded-2xl border border-slate-200 bg-white p-4"
              >
                <h3 className="text-sm font-semibold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-600">{item.text}</p>
              </motion.article>
            ))}
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {audiences.map((item) => {
            const Icon = item.icon;
            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45 }}
                className="group relative overflow-hidden rounded-[28px] border border-slate-200 bg-white p-7 shadow-[0_12px_30px_rgba(15,23,42,0.05)] transition hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(15,23,42,0.12)]"
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#1f4f6b,#e2b84a,#1f4f6b)] opacity-80" />
                <div className="mb-5 inline-flex rounded-2xl bg-slate-100 p-3 text-slate-700 transition group-hover:bg-slate-950 group-hover:text-white">
                  <Icon size={22} />
                </div>
                <h2 className="text-2xl font-semibold text-slate-950">{item.title}</h2>
                <p className="mt-4 text-base leading-7 text-slate-600">{item.description}</p>
                <p className="mt-4 rounded-xl bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">{item.value}</p>
              </motion.article>
            );
          })}
        </div>
      </section>

      <section id="ai-guide" className="border-y border-slate-200 bg-[linear-gradient(180deg,#ffffff,#f8fbff)]">
        <div className="container-max px-6 py-20 md:px-8">
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-500">Opportunità</p>
              <h2 className="mt-4 font-['Merriweather'] text-4xl leading-tight text-slate-950">
                L’AI conversazionale che trasforma contenuti in esperienze vive.
              </h2>
              <p className="mt-5 max-w-xl text-lg leading-8 text-slate-600">
                Ogni luogo puo diventare un percorso. Ogni percorso puo diventare una conversazione. Glooci aiuta creator,
                musei e organizzazioni a trasformare contenuti culturali in esperienze vive.
              </p>
              <div className="mt-8 rounded-[28px] bg-slate-950 p-6 text-white shadow-[0_20px_45px_rgba(15,23,42,0.2)]">
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl bg-white/10 p-3 text-amber-300">
                    <MessageSquare size={20} />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-[0.2em] text-slate-300">Metodo</p>
                    <p className="mt-2 text-lg font-semibold">
                      Flusso iconico: contenuto → esperienza → guida virtuale → conversazione.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_10px_28px_rgba(15,23,42,0.06)]">
                <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-slate-500">
                  <Orbit size={12} className="text-indigo-600" />
                  Guide virtuali in azione
                </div>
                <div className="space-y-2 text-sm">
                  <div className="rounded-xl bg-slate-100 px-3 py-2 text-slate-700">"Benvenuto nel borgo, vuoi iniziare dal museo o dalla piazza storica?"</div>
                  <div className="rounded-xl bg-indigo-600 px-3 py-2 text-white">"Partiamo dal museo: ti accompagno tra opere, storie e curiosita locali."</div>
                  <div className="rounded-xl bg-slate-100 px-3 py-2 text-slate-700">"Perfetto, mostrami anche i punti vicini da visitare dopo."</div>
                </div>
              </div>
              <div className="mt-6 rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_10px_28px_rgba(15,23,42,0.06)]">
                <div className="flex flex-wrap items-center gap-2">
                  {aiPipeline.map((step, index) => (
                    <div key={step} className="inline-flex items-center gap-2">
                      <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">{step}</span>
                      {index < aiPipeline.length - 1 ? <ArrowRight size={13} className="text-slate-400" /> : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_16px_36px_rgba(15,23,42,0.08)]">
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-cyan-200/50 blur-3xl" />
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-500">
                  <Cpu size={16} className="text-indigo-600" /> AI Guide Console
                </div>
                <div className="space-y-3">
                  <div className="max-w-[88%] rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-700">
                    Raccontami questo luogo in modo semplice e coinvolgente.
                  </div>
                  <div className="ml-auto max-w-[88%] rounded-2xl bg-indigo-600 px-4 py-3 text-sm text-white">
                    Benvenuto. Ti guido tappa per tappa con storia, curiosita e media interattivi.
                  </div>
                  <div className="max-w-[78%] rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-700">
                    Mostrami il percorso vicino con i punti principali.
                  </div>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
              {capabilityGroups.map((item, index) => (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="rounded-[26px] border border-slate-200 bg-[linear-gradient(180deg,_#ffffff,_#f8fafc)] p-6 shadow-[0_12px_26px_rgba(15,23,42,0.05)]"
                >
                  <div className="mb-4 inline-flex rounded-full bg-slate-950 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white">
                    0{index + 1}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-950">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
                </motion.article>
              ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="target" className="container-max px-6 py-20 md:px-8">
        <div className="mb-8 flex items-center gap-3">
          <Orbit className="text-sky-600" size={20} />
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Per chi costruisce esperienze</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {targetSegments.map((segment, index) => (
            <motion.article
              key={segment.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.35, delay: index * 0.05 }}
              whileHover={{ y: -3 }}
              className="rounded-3xl border border-slate-200/80 bg-white/75 p-6 shadow-[0_14px_34px_rgba(15,23,42,0.06)] backdrop-blur transition"
            >
              <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-950">
                <Compass size={15} className="text-sky-600" />
                {segment.title}
              </h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{segment.text}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section id="community" className="border-y border-slate-200 bg-[linear-gradient(180deg,#ffffff,#f6fbff)]">
        <div className="container-max px-6 py-20 md:px-8">
          <div className="mb-10 flex items-center gap-3">
            <Users2 size={19} className="text-indigo-600" />
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-500">Marketplace & community</p>
          </div>
          <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
            <div className="rounded-[32px] border border-slate-200 bg-white p-7 shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
              <h3 className="text-3xl font-semibold text-slate-950">Una piattaforma che cresce con i territori</h3>
              <p className="mt-4 text-base leading-8 text-slate-600">
                Glooci valorizza contenuti, creator e organizzazioni in un ecosistema aperto: creazione autonoma,
                pubblicazione, distribuzione verso utenti finali e opportunita B2B/B2B2C.
              </p>
              <div className="mt-5 rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#f8fbff,#ffffff)] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Piattaforma viva</p>
                <p className="mt-2 text-sm leading-7 text-slate-600">
                  Creator che pubblicano esperienze, musei che attivano guide virtuali, territori che raccontano luoghi,
                  utenti che esplorano e conversano. Questo e il cuore Glooci.
                </p>
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {[
                  { label: 'Creazione autonoma', icon: Rocket },
                  { label: 'Distribuzione esperienze', icon: Globe2 },
                  { label: 'Percorsi geolocalizzati', icon: MapPinned },
                  { label: 'Storytelling AI', icon: Bot },
                  { label: 'Cataloghi modulari', icon: Layers3 },
                  { label: 'Connessioni community', icon: Store },
                ].map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700"
                  >
                    <item.icon size={15} className="text-sky-600" />
                    {item.label}
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {experienceShowcase.length > 0 ? experienceShowcase.map((item) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.35 }}
                  className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.07)]"
                >
                  <img src={item.image} alt={`Esperienza ${item.name}`} loading="lazy" className="h-40 w-full object-cover transition duration-500 group-hover:scale-105" />
                  <div className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-700">
                    <Crosshair size={10} className="text-sky-600" />
                    Live
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                    <p className="mt-1 text-xs text-slate-500">{item.city || 'Territorio digitale'}</p>
                  </div>
                </motion.article>
              )) : (
                <div className="rounded-3xl border border-dashed border-slate-300 bg-white/70 p-8 text-sm text-slate-500 sm:col-span-2">
                  Le anteprime esperienze verranno caricate automaticamente dagli endpoint media della piattaforma.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="container-max px-6 py-20 md:px-8">
        <div className="overflow-hidden rounded-[36px] border border-slate-200 bg-[linear-gradient(135deg,_#112433,_#173b53_55%,_#0b1620)] p-8 text-white shadow-[0_24px_60px_rgba(15,23,42,0.24)] md:p-10">
          <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white/90">
                <Ticket size={15} className="text-amber-300" />
                Porta il tuo progetto dentro Glooci
              </div>
              <h2 className="font-['Merriweather'] text-4xl leading-tight md:text-5xl">
                Crea la tua esperienza premium con Glooci e portala live in tempi rapidi.
              </h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-200">
                Dalla prima demo alla pubblicazione: supportiamo setup, design narrativo e attivazione commerciale
                per musei, territori, creator e organizzazioni.
              </p>
              <p className="mt-4 text-sm font-medium text-slate-300">
                Obiettivo: trasformare ogni luogo in un’esperienza che le persone possano esplorare, ascoltare e vivere.
              </p>
            </div>

            <div className="rounded-[30px] border border-white/10 bg-white/10 p-6 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm uppercase tracking-[0.22em] text-slate-300">Glooci Platform</p>
                  <p className="mt-3 text-5xl font-bold text-white">Richiedi demo</p>
                  <p className="mt-2 text-sm text-slate-300">call strategica con il team</p>
                </div>
                <div className="rounded-full bg-amber-400 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-950">
                  premium
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {['Scopri la piattaforma', 'Definisci il percorso migliore', 'Attiva il progetto con onboarding', 'Scala verso B2B/B2B2C']
                  .map((item) => (
                    <div key={item} className="flex items-center gap-3 text-sm text-slate-100">
                      <Star size={15} className="text-amber-300" />
                      <span>{item}</span>
                    </div>
                  ))}
              </div>

              <div className="mt-8 flex flex-col gap-3">
                <a
                  href={pricingMailto}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-4 text-base font-semibold text-slate-950 transition duration-300 hover:-translate-y-0.5 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  Richiedi una demo
                  <ArrowRight size={18} />
                </a>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 px-5 py-4 text-base font-semibold text-white transition duration-300 hover:-translate-y-0.5 hover:bg-white/10"
                >
                  Entra in piattaforma
                </Link>
                <a
                  href={pricingMailto}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-amber-300/60 px-5 py-4 text-base font-semibold text-amber-200 transition duration-300 hover:-translate-y-0.5 hover:bg-amber-300/10"
                >
                  Contatto commerciale
                  <Mail size={17} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white/70">
        <div className="container-max flex flex-col gap-5 px-6 py-8 text-sm text-slate-500 md:flex-row md:items-center md:justify-between md:px-8">
          <div>
            <p className="font-semibold text-slate-700">Glooci Platform</p>
            <p className="mt-1">Creator, musei, territori, eventi e organizzazioni culturali.</p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <a href={eduMailto} className="hover:text-slate-900">Piano EDU</a>
            <a href={pricingMailto} className="hover:text-slate-900">Richiedi demo</a>
            <Link to="/login" className="hover:text-slate-900">Login</Link>
            <a href={PROJECT_CREATOR.website} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">
              by {PROJECT_CREATOR.name}
            </a>
          </div>
        </div>
      </footer>

      <div className="fixed bottom-3 left-3 right-3 z-40 flex gap-2 rounded-2xl border border-white/70 bg-white/90 p-2 shadow-[0_10px_30px_rgba(15,23,42,0.16)] backdrop-blur lg:hidden">
        <a
          href={pricingMailto}
          className="flex-1 rounded-xl bg-slate-950 px-3 py-2 text-center text-sm font-semibold text-white"
        >
          Richiedi demo
        </a>
        <Link
          to="/login"
          className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-center text-sm font-semibold text-slate-800"
        >
          Accedi
        </Link>
      </div>
    </div>
  );
};

export default Landing;
